import React from 'react';
import { Button, Form, HStack, SubmitButton, Heading } from '@medely/ui-kit';
import { EducationFormFields } from './EducationFormFields';
import { educationFormSchema, EducationFormValidationSchemaFieldNames } from './schema';
import { useAssignmentRequestsForEducationForm } from './graphql';
import { IResumeItem } from '@medely/types';
import { useEducationFormMutations } from './mutations';
import { EducationItemFragment } from '../resumeBuilder/components/graphql';
import { FragmentType } from '@mp/graphql/generated';
import { useKeyboardLayout } from '@mp/hooks';
import { RenderModalOrPageBottomLayout } from '../RenderModalOrPageBottomLayout';

type EducationFormProps = {
  assignmentId?: string;
  education?: IResumeItem | FragmentType<typeof EducationItemFragment>;
  onSuccess: () => void;
  isModal?: boolean;
  titleText?: string;
};

export const EducationForm = ({
  assignmentId,
  education,
  onSuccess,
  isModal = false,
  titleText,
}: EducationFormProps) => {
  const { assignmentRequests } = useAssignmentRequestsForEducationForm(Number(assignmentId));
  const { addEducation, editEducation, isLoading } = useEducationFormMutations();
  const { isKeyboardVisible } = useKeyboardLayout();

  const assignmentChecklistVariant = !!assignmentId && !!education;

  const assignment_request_id = !!assignmentRequests?.length ? assignmentRequests[0].id : null;

  const handleSubmit = (values: EducationFormValidationSchemaFieldNames & { id?: number }) => {
    if (education) {
      editEducation(
        {
          values,
          assignment_request_id,
        },
        {
          onSuccess: () => {
            onSuccess();
          },
        },
      );
    } else {
      addEducation(
        {
          values,
          assignment_request_id,
        },
        {
          onSuccess: () => {
            onSuccess();
          },
        },
      );
    }
  };

  const footerActions = (
    <HStack pt={2} gap={isModal ? 2 : 0} justifyContent="space-between">
      {isModal && (
        <Button
          onClick={() => onSuccess()}
          disabled={isLoading}
          variant="outlined"
          fullWidth
          testId="education-modal-back-button"
        >
          Back
        </Button>
      )}
      {!isKeyboardVisible && (
        <SubmitButton
          fullWidth
          disabled={isLoading}
          loading={isLoading}
          testId="education-modal-save-button"
        >
          Save
        </SubmitButton>
      )}
    </HStack>
  );

  return (
    <Form handleSubmit={handleSubmit} schema={educationFormSchema} defaultValues={education}>
      {!isModal && titleText && (
        <Heading size="xs" color="text.primary">
          {titleText}
        </Heading>
      )}
      <RenderModalOrPageBottomLayout
        isModal={isModal}
        footerActions={footerActions}
        formComponent={
          <EducationFormFields assignmentChecklistVariant={assignmentChecklistVariant} />
        }
      />
    </Form>
  );
};
